import React from 'react';

import { Icon, Sheet, Skeleton } from '../../../../components';
import type { SiteGame } from '../../site-games';
import { GameListGrid } from '../GameListGrid';
import { ListActions } from '../ListActions';
import { Note, NoteText, Loading, Failed } from './GameListSheet.styles';

/** The open list's publish as the sheet offers it: whether the service publishes for the brand, and undoing the last
 *  publish (Revert to initial state). The list itself is published with the rest of the page, by the Game Lists tab's
 *  Publish changes. */
export type GameListSheetPublish = {
  /** The service can publish for this brand. */
  available: boolean;
  /** There is a publish to undo. */
  canUndo: boolean;
  writing: 'publish' | 'revert' | null;
  onUndo: () => void;
};

export type GameListSheetProps = {
  /** The list's display name (e.g. "New games"). */
  title: string;
  /** The games in their current (draft) order; null while the list is being read from the site. */
  games: SiteGame[] | null;
  /** The site did not send the list. */
  failed: boolean;
  providerNames: Record<string, string>;
  /** The order differs from the site's own. */
  dirty: boolean;
  onReorder: (ids: string[]) => void;
  /** Back to the site's own order. */
  onReset: () => void;
  /** The sheet closed (Preview on site, the close button, Escape): the caller applies the order to the preview. */
  onClose: () => void;
  /** Preview on site; by default it closes the sheet, which shows the order in the preview behind it. */
  onPreview?: () => void;
  /** Undo publish for this list; without it publishing is not available here. */
  publish?: GameListSheetPublish;
};

const LOADING_TILES = [0, 1, 2, 3, 4, 5];

/**
 * A game list opened from the live site preview: the same reorder surface as the Game Lists tab (grid or
 * phone rows, live filter, move menus), laid out for a dialog, with the same row on top: the filter, then
 * the list's own actions (Preview on site, Publish, and Revert to initial state behind the kebab),
 * pinned while the games scroll.
 * Closing hands the order back to the preview.
 */
export const GameListSheet = ({
  title,
  games,
  failed,
  providerNames,
  dirty,
  onReorder,
  onReset,
  onClose,
  onPreview,
  publish,
}: GameListSheetProps): React.ReactElement => {
  const closeRef = React.useRef<(() => void) | null>(null);
  const close = React.useCallback((): void => closeRef.current?.(), []);

  const renderActions = (compact: boolean): React.ReactNode => (
    <ListActions
      compact={compact}
      canRevert={dirty || Boolean(publish?.available && publish.canUndo)}
      onRevert={() => {
        if (dirty) onReset();
        if (publish?.available && publish.canUndo) publish.onUndo();
      }}
      onPreview={onPreview ?? close}
      writing={publish?.writing ?? null}
    />
  );

  const renderBody = (): React.ReactElement => {
    if (games) {
      return (
        <GameListGrid
          games={games}
          providerNames={providerNames}
          onReorder={onReorder}
          placement="dialog"
          filterActions={renderActions}
        />
      );
    }
    if (failed) return <Failed text="We couldn't load this list" />;
    return (
      <Loading aria-busy="true">
        {LOADING_TILES.map((i) => (
          <Skeleton key={i} block height={188} radius={14} />
        ))}
      </Loading>
    );
  };

  return (
    <Sheet
      title="Reorder {{list}}"
      titlePlaceholders={{ list: title }}
      onClose={onClose}
      closeHandle={closeRef}
      maxWidth="1040px"
      maxHeight="100%"
    >
      {/* The sheet publishes nothing (the Game Lists tab's Publish changes does), so it speaks of publishing only where
          that is not available at all. */}
      {publish?.available ? null : (
        <Note>
          <Icon name="info" size={14} />
          <NoteText text="Changes show in this preview only. Publishing game lists is not available yet." />
        </Note>
      )}
      {renderBody()}
    </Sheet>
  );
};

export default GameListSheet;
